import styled from "styled-components";
import WithDirection from "@iso/lib/helpers/rtl";

const VisitorPageWrapper = styled.div`
  width: 100%;
  height: 100%;

  .visitor-box {
    padding: 20px 24px;
    /* The page canvas is forced white while this page is mounted (see
       the isomorphicContent override in Visitor.js), so the Box border
       would read as a stray outline on an otherwise flat page. */
    border: none;
  }

  /* ------------------------------ breadcrumb ---------------------- */
  .visitor-breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 13px;
    color: #667085;
    margin-bottom: 16px;

    .crumb-sep { color: #98a2b3; }
    .crumb-current { color: #101828; font-weight: 500; }
    .crumb-property { color: #667085; }
  }

  /* -------------------------------- header ------------------------ */
  .visitor-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    flex-wrap: wrap;
    gap: 12px;
    margin-bottom: 20px;
  }
  .visitor-title {
    font-size: 28px;
    font-weight: 700;
    color: #101828;
    margin: 0 0 2px;
  }
  .visitor-subtitle {
    font-size: 14px;
    color: #667085;
  }
  .header-actions {
    display: flex;
    gap: 12px;
    align-items: center;

    .property-option {
      min-width: 240px;
      max-width: 300px;
    }
    .btn-addVisitor {
      background-color: #0b2a1b !important;
      border-color: #0b2a1b;
      height: 40px;
      border-radius: 8px;
      font-weight: 500;
      &:hover { opacity: 0.85; }
    }
  }
  .property-select-loading {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.6);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 10;
  }

  /* ------------------------------ stat cards ---------------------- */
  .stat-cards {
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 16px;
    margin-bottom: 24px;
  }
  .stat-card {
    display: flex;
    align-items: center;
    gap: 14px;
    background: #ffffff;
    border: 1px solid #eaecf0;
    border-radius: 12px;
    padding: 18px 20px;
    box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
  }
  .stat-icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;

    &.icon-total    { background: #eff4ff; color: #2563eb; }
    &.icon-onsite   { background: #ecfdf3; color: #16a34a; }
    &.icon-expected { background: #fff6ed; color: #ea580c; }
    &.icon-pending  { background: #fef3f2; color: #dc2626; }
  }
  .stat-value {
    font-size: 22px;
    font-weight: 700;
    color: #101828;
    line-height: 1.2;
  }
  .stat-label {
    font-size: 13px;
    color: #667085;
  }

  /* --------------------------------- tabs ------------------------- */
  .visitor-tabs {
    display: flex;
    gap: 4px;
    border-bottom: 1px solid #eaecf0;
    margin-bottom: 16px;
  }
  .visitor-tab {
    appearance: none;
    background: none;
    border: none;
    border-bottom: 2px solid transparent;
    padding: 10px 14px;
    font-size: 14px;
    font-weight: 500;
    color: #667085;
    cursor: pointer;
    display: flex;
    align-items: center;
    gap: 8px;

    &:hover { color: #101828; }
    &.active {
      color: #101828;
      font-weight: 600;
      border-bottom-color: #0b2a1b;
    }
  }
  .tab-count {
    background: #f2f4f7;
    color: #344054;
    border-radius: 999px;
    font-size: 12px;
    font-weight: 600;
    padding: 1px 8px;
    line-height: 18px;

    &.active {
      background: #101828;
      color: #ffffff;
    }
  }

  /* ------------------------------ filter bar ---------------------- */
  .filter-bar {
    display: flex;
    align-items: center;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
  }
  .visitor-search {
    max-width: 320px;
    height: 40px;
    border-radius: 8px;
    border: 1px solid #eaecf0;
    background: #ffffff;
    box-shadow: none;

    /* The affix wrapper above carries the visible border; the inner
       input must stay borderless or the two stack into a dark double
       border (a global input style gives it a dark outline). */
    .ant-input {
      font-size: 14px;
      border: none !important;
      outline: none !important;
      box-shadow: none !important;
      background: transparent;
    }

    &.ant-input-affix-wrapper-focused,
    &:focus,
    &:hover {
      border-color: #0b2a1b;
      box-shadow: none;
    }
  }
  .search-icon-prefix { color: #98a2b3; }
  .search-clear { color: #98a2b3; cursor: pointer; }
  .filter-icon { color: #667085; font-size: 16px; }
  .filter-select {
    min-width: 140px;
    .ant-select-selector {
      border-radius: 8px !important;
      height: 40px !important;
      display: flex;
      align-items: center;
    }
  }

  /* -------------------------------- table ------------------------- */
  .visitor-table-wrap {
    border: 1px solid #eaecf0;
    border-radius: 12px;
    background: #ffffff;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.1), 0 1px 2px rgba(16, 24, 40, 0.06);
    overflow-x: auto;
  }
  .visitor-table {
    width: 100%;
    border-collapse: collapse;
    min-width: 1080px;

    thead th {
      text-align: left;
      font-size: 12px;
      font-weight: 600;
      letter-spacing: 0.04em;
      text-transform: uppercase;
      color: #667085;
      background: #f9fafb;
      padding: 12px 16px;
      border-bottom: 1px solid #eaecf0;
      white-space: nowrap;
    }
    thead th.actions-head { text-align: right; }

    tbody td {
      padding: 14px 16px;
      border-bottom: 1px solid #f2f4f7;
      vertical-align: middle;
      font-size: 14px;
      color: #344054;
    }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:hover td { background: #f9fafb; }
  }

  .visitor-cell {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .visitor-avatar {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    color: #ffffff;
    font-weight: 600;
    font-size: 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  .visitor-name {
    font-weight: 600;
    color: #101828;
    line-height: 1.3;
  }
  .visitor-sub {
    font-size: 12.5px;
    color: #667085;
    line-height: 1.4;
    word-break: break-all;
  }

  .type-badge {
    display: inline-block;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 500;
    padding: 2px 10px;
    white-space: nowrap;

    &.type-contractor { background: #fef7e0; color: #b45309; }
    &.type-guest      { background: #eff4ff; color: #2563eb; }
    &.type-delivery   { background: #f4f0ff; color: #7c3aed; }
    &.type-inspector  { background: #ecfdf3; color: #16a34a; }
    &.type-other      { background: #f2f4f7; color: #475467; }
  }

  .status-pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 999px;
    font-size: 12.5px;
    font-weight: 500;
    padding: 2px 10px;
    white-space: nowrap;

    .status-dot {
      width: 7px;
      height: 7px;
      border-radius: 50%;
      display: inline-block;
    }
    &.status-onsite   { background: #ecfdf3; color: #067647; .status-dot { background: #17b26a; } }
    &.status-expected { background: #f0f9ff; color: #026aa2; .status-dot { background: #0ba5ec; } }
    &.status-departed { background: #f2f4f7; color: #475467; .status-dot { background: #98a2b3; } }
    &.status-overdue  { background: #fef3f2; color: #b42318; .status-dot { background: #f04438; } }
  }

  .ts-cell { white-space: nowrap; color: #344054; }
  .ts-cell.muted { color: #98a2b3; }
  .scheduled-hint { color: #026aa2; font-size: 13px; }

  .induction {
    display: inline-flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 6px;
    font-size: 13px;
    font-weight: 500;
    white-space: nowrap;

    &.complete      { color: #067647; }
    /* Submitted and waiting on a verifier. Amber, not green: the visitor has
       finished their part, but nobody has accepted it yet. */
    &.pending       { color: #b45309; }
    &.sent          { color: #1d4ed8; }
    /* Expected and not done. Grey rather than red — it is outstanding, not
       failed, and the row should not shout until it expires or is refused. */
    &.not-started   { color: #667085; }
    /* Someone looked and said no. Red like expired, and never merged with it:
       one was refused, the other lapsed. */
    &.rejected      { color: #b42318; }
    /* Nothing was ever asked of this person. As quiet as not-recorded, because
       it is not an action for anybody. */
    &.empty         { color: #d0d5dd; font-weight: 400; }
    /* Still valid, but inside the 30-day window — amber warns without
       reading as a failure, matching the contractor registers' expiry pill. */
    &.expiring      { color: #b54708; }
    &.expired       { color: #b42318; }
    /* Predates induction tracking: deliberately quiet, so it reads as
       "no record" rather than as an outstanding action. */
    &.not-recorded  { color: #98a2b3; font-weight: 400; }
  }

  /* Per-requirement breakdown behind the "N of M" badge. */
  .induction-breakdown {
    max-width: 280px;
    font-size: 13px;

    .induction-breakdown-row {
      padding: 2px 0;
      color: #344054;
      white-space: normal;
    }
  }

  /* When the induction was completed — secondary to the status itself, and on
     its own line so it never widens the column. (The form name used to sit here
     too; the column now shows status and date only.) */
  .induction-date {
    display: block;
    width: 100%;
    font-size: 12px;
    font-weight: 400;
    color: #667085;
    white-space: normal;
  }

  .actions-cell {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
  }
  .checkout-btn {
    border-radius: 8px;
    font-size: 13px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    border-color: #d0d5dd;
    color: #344054;

    &:hover:not(:disabled) {
      border-color: #0b2a1b;
      color: #0b2a1b;
    }
  }
  .overflow-btn {
    border: none;
    box-shadow: none;
    color: #667085;
    border-radius: 8px;
  }

  .pagination-wrap {
    padding: 16px;
    display: flex;
    justify-content: flex-end;
  }
  .ant-pagination-item-active {
    border-color: #0b2a1b;
    a { color: #0b2a1b; }
  }
  .ant-pagination-item:focus, .ant-pagination-item:hover {
    border-color: #0b2a1b;
    a { color: #0b2a1b; }
  }
  .ant-pagination-item-link:hover {
    border-color: #0b2a1b;
    color: #0b2a1b;
  }
  .ant-spin-dot-item { background-color: #0b2a1b; }

  /* ------------------------------ responsive ---------------------- */
  @media only screen and (max-width: 1100px) {
    .stat-cards { grid-template-columns: repeat(2, 1fr); }
  }
  @media only screen and (max-width: 640px) {
    .visitor-box { padding: 14px; }
    .stat-cards { grid-template-columns: 1fr; }
    .visitor-header { flex-direction: column; align-items: stretch; }
    .header-actions {
      flex-direction: column;
      align-items: stretch;
      .property-option { min-width: 100%; }
    }
    .visitor-tabs { overflow-x: auto; }
    .filter-bar {
      .visitor-search { max-width: 100%; width: 100%; }
    }
  }
`;

export default WithDirection(VisitorPageWrapper);
